<script>
  import InternalAdmin from '@ajax/internal_admin';
  import { getCurrentUserStore } from '@stores/current_user';
  import Button from '@components/Button.svelte';
  import tippy from 'tippy.js';
  import CollapsibleSection from './CollapsibleSection.svelte';
  import Modal from '@components/Modal.svelte';
  import AdminReferralInfo from '@pages/profile/components/admin-sidebar/AdminReferralInfo.svelte';
  import { onMount } from 'svelte';
  import { ErrorIconSvg } from '@utils/icons';
  import Icon from '@components/Icon.svelte';
  import AdminCreateBonusTransactionModal from '@pages/profile/components/admin-sidebar/AdminCreateBonusTransactionModal.svelte';

  export let user_id;
  export let group_id = null;
  export let mobile = false;

  let context = group_id ? 'group' : 'user';
  let userInfo = {};
  let activityInfo = {};
  let recentActivityInfo = {};
  let personalizationInfo = {};
  let personalizationCategories = {};
  let expanded = false;
  let currentUser = getCurrentUserStore('default');
  let demoButtonText = currentUser.user_content_hide ? 'Turn off demo mode' : 'Turn on demo mode';
  let demoButtonStyle = currentUser.user_content_hide ? 'red' : 'purple';
  let showReferralModal = false;
  let showingReferrals = true;
  let referredUsers = [];
  let referralUsersLoading = true;
  let referralCount = 0;
  let showBonusTransactionModal = false;
  $: referralCount = userInfo?.growth?.number_of_signups_using_code || 0;

  let getUserInfo = async function () {
    if (userInfo && userInfo.id) {
      return;
    }

    userInfo = group_id ? await InternalAdmin.groupInfo(user_id, group_id) : await InternalAdmin.userInfo(user_id);
  };

  let getActivityInfo = async function () {
    if (activityInfo && activityInfo.id) {
      return;
    }

    activityInfo = await InternalAdmin.activityInfo(user_id);
  };

  let getRecentActivityInfo = async function () {
    if (recentActivityInfo && recentActivityInfo.id) {
      return;
    }

    recentActivityInfo = await InternalAdmin.recentActivityInfo(user_id);
  };

  let getPersonalizationInfo = async function () {
    if (personalizationInfo && personalizationInfo.id) {
      return;
    }

    personalizationInfo = await InternalAdmin.personalizationInfo(user_id);

    setupPersonalizationCategories(personalizationInfo.categories);
  };

  let setupPersonalizationCategories = function (categories) {
    personalizationCategories = {};

    if (!categories) {
      return;
    }

    categories.forEach((c) => {
      let cat = c[0];
      let weight = c[1];

      let allCategories = cat.split(' > ');

      let curr = personalizationCategories;
      let idx = 1;

      allCategories.forEach((x) => {
        if (!curr[x]) {
          curr[x] = {};
        }

        if (idx == allCategories.length) {
          curr[x]['_weight_'] = weight;
        } else {
          curr = curr[x];
          idx += 1;
        }
      });
    });

    personalizationCategories = personalizationCategories;
  };

  let setupTippy = (node, data) => {
    if (mobile) {
      return;
    }

    let instance = tippy(node, {
      content: data,
      allowHTML: true,
      placement: 'bottom-start',
    });

    return {
      destroy() {
        try {
          instance.destroy();
        } catch {}
      },
    };
  };

  let getReferredUsers = async function () {
    try {
      referralUsersLoading = true;
      referredUsers = await InternalAdmin.referredUsers(user_id);
      referralUsersLoading = false;
    } catch (error) {
      console.error('Error fetching referral users:', error);
    }
  };

  const fetchAllData = async () => {
    await getUserInfo();
    await getActivityInfo();
    await getRecentActivityInfo();
    await getPersonalizationInfo();
    await getReferredUsers();
  };

  onMount(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const showReferral = urlParams.get('admin_show_referral');
    const showConversions = urlParams.get('admin_show_conversions');

    if (showReferral || showConversions) {
      expanded = true;
      fetchAllData().then(() => {
        showReferralModal = true;
        showingReferrals = showReferral ? true : false;
      });
    }
  });
</script>

<style lang="scss">
  @import '../../../vendor/design-system.scss';
  .mobile {
    .expand-button {
      top: 10px;
      right: 10px;
    }
  }
  .expand-button {
    position: absolute;
    right: 40px;
    top: 0px;
    padding: 15px 10px;
    cursor: pointer;
    background-color: white;
    width: 40px;

    &:hover {
      img {
        opacity: 50%;
      }
    }

    &.expanded {
      left: -40px;
    }
  }
  .admin-info {
    position: fixed;
    z-index: 10;
    background-color: white;
    right: 0px;
    box-shadow: 0px 16.33px 74.22px 0px rgba(0, 0, 0, 0.09);

    .admin-inner {
      width: 332px;

      max-height: calc(100vh - 130px);
      overflow-y: auto;
    }

    :global(.text) {
      @include typeface-b2($font-weight-medium);
      color: $neutral-gray;
      margin: 0px;
      line-height: 24px;
    }

    :global(.black) {
      color: $black;
    }

    :global(.red-dark) {
      color: $red-dark;
    }
    :global(.green) {
      color: $green;
    }

    :global(.bold) {
      font-weight: $font-weight-bold;
    }
    :global(.gap) {
      padding-top: 6px;
      padding-bottom: 6px;
    }

    :global(.line) {
      height: 1px;
      background: $neutral-input-gray;
    }

    :global(.subheader) {
      text-transform: uppercase;
      font-weight: $font-weight-bold;
      font-size: 12px;
      color: $neutral-gray;
      margin: 0px;
      margin-bottom: 10px;
    }

    :global(.social-row) {
      display: flex;
      align-items: center;
    }

    :global(a) {
      color: $blue !important;
    }

    :global(a:hover) {
      color: $dark-blue !important;
    }

    :global(.section + .section) {
      border-top: 1px solid $neutral-input-gray;
    }

    :global(.section) {
      padding: 20px 15px;
    }

    :global(.loading-wrapper) {
      width: 332px;
      display: flex;
      justify-content: center;
      margin: 0 auto;
      height: calc(100vh - 130px);
    }

    :global(.loading-wrapper .loading) {
      margin-top: 50px;
      font-size: 32px;
    }

    :global(.activity-loading-wrapper) {
      width: 332px;
      display: flex;
      justify-content: center;
      margin: 0 auto;
      height: 75px;
    }

    :global(.activity-loading-wrapper .loading) {
      margin-top: 25px;
      margin-bottom: 25px;
      font-size: 32px;
    }

    .clickable {
      cursor: pointer;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  * :global(.desktop-modal__container) {
    border-radius: 16px !important;
    min-width: 1100px !important;
    height: calc(100vh - 80px) !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    max-height: 860px !important;
  }

  .modal-headers {
    display: flex;
    gap: 24px;
    margin-bottom: 24px;

    :global(.modal__title) {
      opacity: 0.5;

      &.active {
        opacity: 1;
      }
    }
  }
</style>

<div class="admin-info" class:mobile={mobile}>
  <div
    class="expand-button {expanded ? 'expanded' : 'closed'}"
    on:click={() => {
      expanded = !expanded;
      if (expanded) {
        fetchAllData();
      }
    }}>
    <div class="inner">
      {#if expanded}
        <img
          alt="x-icon"
          style="margin-left: 3px; height: 16px; width: 16px; margin-right: 14px"
          src="/images/Close-Search.svg" />
      {:else}
        <img
          alt="x-icon"
          style="margin-left: 3px; height: 16px; width: 16px; margin-right: 14px"
          src="/images/LeftArrow.svg" />
      {/if}
    </div>
  </div>
  {#if expanded}
    <div class="admin-inner">
      {#if userInfo && userInfo.id}
        <div class="section">
          <h4 style="margin: 0px; display: flex; justify-content: space-between; align-items: center">
            <span style="display: flex; align-items: center; line-height: 24px; gap: 4px">
              {userInfo.name}
              {#if userInfo.spam_user}
                <span use:setupTippy={'Spam user'} style="display: flex; align-items: center;">
                  <Icon name="error" size="24" path={ErrorIconSvg} color="red" />
                </span>
              {/if}
            </span>
            <a href={userInfo.admin_edit_url} target="_blank">{userInfo.id}</a>
          </h4>
          <p class="text" style="display: flex; justify-content: space-between">
            <span>{userInfo.username}</span>
            <a target="_blank" href="{userInfo.magic_link}&__benable_user=true"
              ><img src="/images/text-input/link.svg" alt="link-icon" />
            </a>
          </p>
          <p class="text">{userInfo.email}</p>
          {#if userInfo.country_code}
            <p class="text">{userInfo.country_code}</p>
          {/if}
          <div class="gap" />
          <p class="text">Joined <span class="black">{userInfo.joined}</span></p>
          <p class="text">{@html userInfo.referral_info}</p>
          <div class="gap" />
          <p class="text">
            <span class="black">{userInfo.list_count}</span> lists, <span class="black">{userInfo.rec_count}</span>
            items (<span class="black">{userInfo.affiliate_rec_count}</span> affiliated)
          </p>
          <p class="text">
            <span class="black">{userInfo.lists_with_optimized_badge}</span> lists with optimized badge
          </p>
          <p class="text">
            <span class="black">{userInfo.lists_average_score}</span> average list score
          </p>
          <p class="text">
            <span class="black">{userInfo.profile_score}</span> profile score
          </p>
          <div class="social-row">
            {#if userInfo?.wrapped_information?.token}
              {#if userInfo.wrapped_information.self_views > 0}
                <p style="display:flex; margin-right: 10px" class="text">
                  {userInfo.wrapped_information.month}&nbsp;<a target="_blank" href={userInfo.wrapped_information.url}
                    >wrapped</a>
                </p>
                &nbsp;
                <p class="text">
                  {userInfo.wrapped_information.self_views} self views, <br />
                  {userInfo.wrapped_information.other_views}
                  other views
                </p>
              {:else}
                <img
                  alt="x-icon"
                  style="margin-left: 3px; height: 16px; width: 16px; margin-right: 14px"
                  src="/images/Close-Search.svg" />
                <p style="display:flex" class="text">
                  Has not viewed {userInfo.wrapped_information.month}&nbsp;
                  <a target="_blank" href={userInfo.wrapped_information.url}>wrapped</a>
                </p>
              {/if}
            {/if}
          </div>
        </div>
        <div class="section">
          <h3 class="subheader">ACTIVITY</h3>
          {#if !activityInfo.id}
            <div class="activity-loading-wrapper" style="width: 330px">
              <div class="loading" style="">
                <i class="fa fa-spin fa-spinner" />
              </div>
            </div>
          {:else}
            {#if activityInfo.days_since_last_event || activityInfo.days_since_last_event == 0}
              <p class="text">
                <span class="black">{activityInfo.days_since_last_event} days</span>
                since last active
              </p>
            {/if}
            {#if activityInfo.days_since_last_profile_or_list_view || activityInfo.days_since_last_profile_or_list_view == 0}
              <p class="text">
                <span class="black">{activityInfo.days_since_last_profile_or_list_view} days</span>
                since last view
              </p>
            {/if}
            <p class="text">
              <span class="black">{activityInfo.thirty_day_session_count} sessions</span>
              last 30 days
            </p>
            <p class="text">
              <span class="black">{activityInfo.recs_added} items added</span>
              last 30 days
            </p>
            <p class="text">
              <span class="black">{activityInfo.time_on_site} time on site</span>
              last 30 days
            </p>
            <p class="text">
              <span class="black">{activityInfo.six_month_session_count} sessions</span>
              last 6 months
            </p>
          {/if}
        </div>

        <CollapsibleSection headerText="Browsing (last 30 days)">
          {#if !recentActivityInfo.id}
            <div class="activity-loading-wrapper" style="width: 330px">
              <div class="loading" style="">
                <i class="fa fa-spin fa-spinner" />
              </div>
            </div>
          {:else}
            <p class="text">
              <span class="black">{recentActivityInfo.discover_view}</span>
              times viewed Discover
            </p>
            <p class="text">
              <span class="black">{recentActivityInfo.discover_category_click}</span>
              Discover categories clicked
            </p>
            <p class="text">
              <span class="black">{recentActivityInfo.discover_list_viewed}</span>
              lists viewed on Discover
            </p>
            <p class="text">
              <span class="black">{recentActivityInfo.lists_opened}</span>
              lists opened
            </p>
            <p class="text">
              <span class="black">{recentActivityInfo.users_viewed}</span>
              users viewed
            </p>
            <p class="text">
              <span class="black">{recentActivityInfo.bookmarks_added}</span>
              bookmarks added
            </p>
            <p class="text">
              <span class="black">{recentActivityInfo.comments_added}</span>
              comments added
            </p>
            <p class="text">
              <span class="black">{recentActivityInfo.likes_added}</span>
              likes added
            </p>
          {/if}
        </CollapsibleSection>
        <CollapsibleSection headerText="Personalization">
          {#if !personalizationInfo.id}
            <div class="activity-loading-wrapper" style="width: 330px">
              <div class="loading" style="">
                <i class="fa fa-spin fa-spinner" />
              </div>
            </div>
          {:else}
            <div style="margin-top: 20px">
              {#each Object.entries(personalizationCategories) as [category, data]}
                {#if data['_weight_']}
                  <p class="text">
                    <span class="black">{category}</span><span style="margin-left: 4px">{data['_weight_']}</span>
                  </p>
                {/if}
                {#each Object.entries(data) as [category, data]}
                  {#if data['_weight_']}
                    <p class="text" style="margin-left: 14px;">
                      <span class="black">{category}</span>
                      <span style="margin-left: 4px">{data['_weight_']}</span>
                    </p>
                  {/if}
                  {#each Object.entries(data) as [category, data]}
                    {#if data['_weight_']}
                      <p class="text" style="margin-left: 28px;">
                        <span class="black">{category}</span>
                        <span style="margin-left: 4px">{data['_weight_']}</span>
                      </p>
                    {/if}
                  {/each}
                {/each}
              {/each}
            </div>
          {/if}
        </CollapsibleSection>

        {#if context == 'user'}
          {#if userInfo.social_info && Object.keys(userInfo.social_info).length > 0}
            <CollapsibleSection headerText="Socials">
              {#if userInfo.social_info.tiktok}
                <div class="social-row" style="display: flex; align-items: center; ">
                  {#each userInfo.social_info.tiktok.links as link}
                    <div style="display: flex; flex-direction: column; width: 24px; height: 24px; margin-right: 10px;">
                      <a target="_blank" href={link}
                        ><img
                          alt="tiktok-icon"
                          style="height: 24px; width: 24px;"
                          src="/images/profile/socials/tiktok.svg" /></a>
                    </div>
                  {/each}

                  <div style="display:flex; flex-direction: column">
                    {#if userInfo.social_info.tiktok.view_count}
                      <p class="text black">{userInfo.social_info.tiktok.view_count} views</p>
                    {/if}
                    {#each userInfo.social_info.tiktok.links_in_bio as link}
                      <a class="text" target="_blank" href={link}>
                        {link}
                      </a>
                    {/each}
                  </div>
                </div>
                <div class="gap" />
              {/if}
              {#if userInfo.social_info.instagram}
                <div class="social-row">
                  <div style="display: flex; align-items: center;">
                    {#each userInfo.social_info.instagram.links as link}
                      <div
                        style="display: flex; flex-direction: column; width: 24px; height: 24px; margin-right: 10px;">
                        <a target="_blank" href={link}
                          ><img
                            alt="instagram-icon"
                            style="height: 24px; width: 24px;"
                            src="/images/profile/socials/instagram.svg" /></a>
                      </div>
                    {/each}
                  </div>
                  <div style="display:flex; flex-direction: column">
                    {#if userInfo.social_info.instagram.follower_count}
                      <p class="text black">{userInfo.social_info.instagram.follower_count} followers</p>
                    {/if}
                    {#each userInfo.social_info.instagram.links_in_bio as link}
                      <a class="text" target="_blank" href={link}>
                        {link}
                      </a>
                    {/each}
                  </div>
                </div>
                <div class="gap" />
              {/if}
              <div class="social-row">
                {#if userInfo.social_info.benable_present}
                  <img
                    alt="checkmark"
                    style="height: 16px; width: 16px; margin-right: 17px"
                    src="/images/follow/checkmark.svg" />
                  <p style="display:flex" class="text">
                    <span class="black">Added </span>&nbsp;to {userInfo.social_info.benable_present}
                  </p>
                {:else}
                  <img
                    alt="x-icon"
                    style="margin-left: 3px; height: 16px; width: 16px; margin-right: 14px"
                    src="/images/Close-Search.svg" />
                  <p style="display:flex" class="text">Has not added Benable</p>
                {/if}
              </div>
            </CollapsibleSection>
          {/if}
          <CollapsibleSection headerText="Growth">
            <p class="text">
              {#if userInfo.growth.invite_last_copied}
                <span class="black">{userInfo.growth.invite_last_copied}</span> last copied invite
              {:else}
                Never copied invite
              {/if}
            </p>
            <div class="gap" />
            <p
              class="text clickable"
              on:click={() => {
                showReferralModal = true;
                showingReferrals = true;
              }}>
              <span class="black">{userInfo.growth.number_of_signups_using_code}</span> referrals
            </p>
            <p
              class="text clickable"
              on:click={() => {
                showReferralModal = true;
                showingReferrals = false;
              }}>
              <span class="black">{userInfo.growth.number_of_conversions}</span> conversions
            </p>
          </CollapsibleSection>
          <CollapsibleSection headerText="Commissions">
            <p class="text">
              <span class="black">${userInfo.commissions.current_balance}</span> current cashback balance
            </p>
            <p class="text">
              <span class="black">${userInfo.commissions.total_lifetime_commissions}</span> lifetime commissions
            </p>
            {#if userInfo.commissions.date_of_last_commission}
              <p class="text">
                <span class="black">{userInfo.commissions.date_of_last_commission}</span> last commission
              </p>
            {/if}
            <div class="gap" />
            <p class="text">
              <span class="black">${userInfo.commissions.total_lifetime_fake_commissions}</span> total bonus commissions
            </p>
            {#if userInfo.commissions.days_since_last_fake_commission || userInfo.commissions.days_since_last_fake_commission == 0}
              <p class="text">
                <span class="black">{userInfo.commissions.days_since_last_fake_commission} days</span> since last bonus commission
              </p>
            {/if}
            {#if userInfo.commissions.next_fake_commission}
              <p class="text">
                <span class="black">${userInfo.commissions.next_fake_commission.amount}</span> commission scheduled for {userInfo
                  .commissions.next_fake_commission.date}
              </p>
            {/if}
            {#if userInfo.commissions.bonus_commission_range}
              <p class="text">
                <span class="black">${userInfo.commissions.bonus_commission_range.value}</span>{userInfo.commissions
                  .bonus_commission_range.queued
                  ? ''
                  : 'last '} bonus commission range
              </p>
            {/if}
          </CollapsibleSection>
        {:else if context == 'group'}
          <div class="section">
            <div style="display: flex; justify-content: space-between">
              <h3 class="subheader">List info</h3>
              <a href={userInfo.group_info.edit_url} target="_blank">Edit</a>
            </div>
            <p class="text">
              ID: <span class="black" style="font-size: 12px;">{userInfo.group_info.id}</span>
            </p>
            <p class="text">
              Created on <span class="black">{userInfo.group_info.created_at}</span>
            </p>
            <p class="text">
              Last updated on <span class="black">{userInfo.group_info.updated_at}</span>
            </p>
            {#if userInfo.group_info.is_optimized}
              <p class="text">
                Optimized <img alt="logo" class="modal-icon" height={15} src="/images/optimized-logo.svg" />
              </p>
            {/if}
            <div class="gap" />
            <p class="text">
              <span class="black">{userInfo.group_info.affiliate_rec_count}</span> affiliated recs
            </p>
            <div class="gap" />
            {#if userInfo.group_info.score}
              <div style="display: flex; justify-content: space-between">
                <p class="text">
                  <span class="black">{userInfo.group_info.score}/5</span> quality score
                </p>
                <a href={userInfo.group_info.score_link} target="_blank">Details</a>
              </div>
              <p class="text" use:setupTippy={userInfo.group_info.score_flags.join('<br />')}>
                <span class="black">{userInfo.group_info.score_flag_count}</span> quality flags
              </p>
            {/if}
            <div class="gap" />
            {#if userInfo.group_info.categories && userInfo.group_info.categories.length > 0}
              <p class="text">Categories</p>
              {#each userInfo.group_info.categories as category}
                <p class="text">
                  <span class="black">{category.name} ({category.confidence})</span>
                </p>
              {/each}
            {/if}
          </div>
        {/if}
        {#if currentUser}
          <div class="section">
            <Button
              text="Create Bonus Transaction"
              colorScheme="black"
              style="margin-bottom: 20px; width: 100%;"
              on:click={() => {
                showBonusTransactionModal = true;
              }} />

            <Button
              text={demoButtonText}
              colorScheme={demoButtonStyle}
              style={mobile ? 'width: 80px;' : 'width: 131px;'}
              on:click={async () => {
                await InternalAdmin.toggleDemoMode();
                location.reload();
              }} />
          </div>
        {/if}
      {:else}
        <div class="loading-wrapper" style="width: 330px">
          <div class="loading" style="">
            <i class="fa fa-spin fa-spinner" />
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if showReferralModal}
    <div class="svelte-modal">
      <Modal
        mobile={mobile}
        on:close={() => {
          showReferralModal = false;
        }}
        show={true}
        showCloseButton={true}
        fadeInDuration={200}
        containerClass={mobile ? 'mobile-modal__container' : 'desktop-modal__container'}>
        <div slot="content">
          <div class="modal-headers">
            <h3
              class="modal__title"
              class:active={showingReferrals}
              on:click={() => (showingReferrals = true)}
              style="cursor: pointer;">
              {referralCount} Referral{referralCount != 1 ? 's' : ''}
            </h3>
            <h3
              class="modal__title"
              class:active={!showingReferrals}
              on:click={() => (showingReferrals = false)}
              style="cursor: pointer;">
              {userInfo.growth.number_of_conversions} Conversion{userInfo.growth.number_of_conversions != 1 ? 's' : ''}
            </h3>
          </div>
          <AdminReferralInfo
            user_id={user_id}
            mobile={mobile}
            users={showingReferrals ? referredUsers : referredUsers.filter((user) => user.converted)}
            totalCount={showingReferrals ? referralCount : userInfo.growth.number_of_conversions}
            loading={referralUsersLoading}
            on:close={() => {
              showReferralModal = false;
            }} />
        </div>
      </Modal>
    </div>
  {/if}

  {#if showBonusTransactionModal}
    <AdminCreateBonusTransactionModal
      showModal={showBonusTransactionModal}
      mobile={mobile}
      user={userInfo}
      on:close={() => {
        showBonusTransactionModal = false;
      }} />
  {/if}
</div>
